import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { listProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CategoryFilter from "@/components/CategoryFilter";
import { Input } from "@/components/ui/input";
import { Search, PackageX } from "lucide-react";

const ProductsPage = () => {
  const { t, lang } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const category = searchParams.get("category") || "all";

  useEffect(() => {
    setLoading(true);
    listProducts(category)
      .then(setProducts)
      .finally(() => setLoading(false));
  }, [category]);

  const filtered = products.filter((p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return p.name_en.toLowerCase().includes(q) || p.name_hi.includes(search);
  });

  const onCat = (c) => {
    if (c === "all") setSearchParams({});
    else setSearchParams({ category: c });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="products-page">
      <div className="mb-6">
        <p className="text-xs font-semibold text-[#D97706] uppercase tracking-widest mb-1">
          {t("browseCategories")}
        </p>
        <h1 className={`font-heading font-bold text-3xl sm:text-4xl text-[#1E293B] ${lang === "hi" ? "font-devanagari" : ""}`}>
          {t("products")}
        </h1>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={t("search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="product-search-input"
            className="h-12 pl-10 bg-white border-[#E2E8F0]"
          />
        </div>
        <CategoryFilter active={category} onChange={onCat} />
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
              <div className="aspect-square bg-[#EFEBE2] animate-pulse" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-[#EFEBE2] rounded animate-pulse" />
                <div className="h-4 bg-[#EFEBE2] rounded w-1/2 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20" data-testid="no-products-message">
          <PackageX className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600">{t("noProducts")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="products-grid">
          {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
};

export default ProductsPage;
